// Results controls (<results-list>). Pure enhancement: the Section
// Rendering API on top of a page that works without it. The host is never
// replaced: scripts in swapped HTML do not run, so listeners live here.
// O porquê de cada escolha está em docs/sections/results.md.

const LOADING = 'loading';

// Behind the backdrop is behind for the keyboard too. Named regions: the
// panel's checkbox and backdrop are its siblings. Also in header.js.
function setPageInert(regions, on) {
  regions.forEach((region) => region?.toggleAttribute('inert', on));
}

// What a filter, a sort or a page change can affect, facets included:
// their counts move with the results. The sort select is left out and
// synced instead: iOS Safari reopens its picker on focus().
const REGIONS = ['.results__facets-inner', '.results__count', '.results__applied', '.results__list'];

class ResultsList extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.sectionId = this.dataset.sectionId;
    // Neither is ever swapped, so caching them is safe.
    this.status = this.querySelector('[data-results-status]');
    this.panelState = this.querySelector('.results__filter-state');
    if (!this.form || !this.sectionId) return;

    // A ordenação aplica sozinha, que é escolha única; os filtros esperam
    // o "Aplicar", porque se marcam três ou quatro de uma vez.
    this.setAttribute('enhanced', '');
    this.normalizeFamilies();

    this.listeners = new AbortController();
    const { signal } = this.listeners;

    this.addEventListener('change', this.handleChange, { signal });
    this.addEventListener('click', this.handleClick, { signal });
    this.addEventListener('submit', this.handleSubmit, { signal });
    document.addEventListener('keydown', this.handleKeydown, { signal });
    window.addEventListener('popstate', this.handlePopstate, { signal });

    // Busca: ordenações que a Shopify não faz nela (search-order.js).
    if (this.dataset.orderScript) {
      this.order = import(this.dataset.orderScript).then((m) => new m.default(this));
      this.order.then((o) => o.sortFor(location.href) && this.render(location.href));
    }
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.pending?.abort();
    // Inert left behind would lock the keyboard out of the page.
    this.setPanelInert(false);
  }

  handleChange = (event) => {
    if (event.target === this.panelState) {
      this.setPanelInert(this.panelState.checked);
      return;
    }
    // Uma família de valores parecidos — "Marrom" por nove marrons — é
    // uma escolha só: vale a primeira caixa, e as outras acompanham.
    const family = event.target.closest('[data-facet-group]');
    if (family) {
      this.syncFamily(family);
      return;
    }
    // Marcar um filtro apenas marca; quem leva à URL é o "Aplicar".
    if (!event.target.closest('.results__sort-select')) return;
    this.go(this.urlFromForm());
  };

  // Não é a sanfona (openGroups): "família" é o conjunto de valores de um
  // controle só. Vale a líder, que nem sempre é a primeira do HTML.
  syncFamily(family, on = family.querySelector('.facets__input')?.checked) {
    family.querySelectorAll('input').forEach((input) => {
      input.checked = !!on;
    });
  }

  // A família é tudo ou nada: uma URL com só parte dela deixaria o
  // controle visível dizendo o contrário do que está filtrado.
  normalizeFamilies() {
    this.querySelectorAll('[data-facet-group]').forEach((family) => {
      if (family.querySelector('input:checked')) this.syncFamily(family, true);
    });
  }

  // O "Aplicar", e o Enter no preço, que é o mesmo envio: troca parcial
  // em vez de recarregar, e fecha o painel do celular.
  handleSubmit = (event) => {
    event.preventDefault();
    this.go(this.urlFromForm());
    if (!this.panelState?.checked) return;
    this.panelState.checked = false;
    // Atribuir checked não dispara change, então o inert vai na mão.
    this.setPanelInert(false);
  };

  // .results__main is never swapped, so inert survives a filter applied
  // with the panel still open.
  setPanelInert(on) {
    setPageInert([document.querySelector('header'), document.querySelector('footer'), this.querySelector('.results__main')], on);
  }

  // Fichas e "Limpar filtros" junto da paginação: tirar um filtro
  // custava um recarregamento.
  handleClick = (event) => {
    const link = event.target.closest(
      '.pagination__item[href], .results__chip[href], .facets__clear[href]',
    );
    if (!link) return;
    // A middle or cmd click means "open elsewhere": leave it alone.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    // Só a paginação rola ao topo: tirar um filtro é continuar onde se
    // estava; trocar de página é ir a outro lugar.
    this.go(link.href, { scroll: link.matches('.pagination__item') });
  };

  // Escape closes the panel: the one thing a checkbox cannot answer.
  handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.panelState?.checked) return;
    this.panelState.checked = false;
    // Assigning checked never fires change, so inert goes by hand here.
    this.setPanelInert(false);
    // The checkbox, not the label: a label is not focusable.
    this.panelState.focus();
  };

  // The address bar holds the whole state, so replaying it is the lot.
  handlePopstate = () => this.render(window.location.href);

  // The form is the single source of truth: filters, price and sort are
  // controls in it, and the panel's checkbox has no name to submit.
  urlFromForm() {
    const params = new URLSearchParams(new FormData(this.form));
    // An untouched price field would still serialize, leaving
    // ?filter.v.price.gte=&...lte= on every filtered URL.
    for (const [key, value] of [...params]) {
      if (value === '') params.delete(key);
    }
    const query = params.toString();
    return query ? `${this.form.action}?${query}` : this.form.action;
  }

  // Same URL never stacks a second history entry.
  go(url, options) {
    if (url !== location.href) history.pushState({}, '', url);
    this.render(url, options);
  }

  async render(url, { scroll = false } = {}) {
    // A newer request wins, or a burst lands in network order.
    this.pending?.abort();
    const request = new AbortController();
    this.pending = request;
    this.setAttribute(LOADING, '');
    this.setAttribute('aria-busy', 'true');

    const focus = this.focusTarget();
    const open = this.openGroups();

    try {
      const target = new URL(url, window.location.origin);
      target.searchParams.set('sections', this.sectionId);

      const response = await fetch(target, { signal: request.signal });
      const data = await response.json();
      const html = data[this.sectionId];
      // A section that fails to render comes back null inside a 200.
      if (!html) throw new Error('section did not render');

      const fresh = new DOMParser().parseFromString(html, 'text/html');
      REGIONS.forEach((selector) => this.swap(fresh, selector));
      this.syncSort(fresh);
      this.normalizeFamilies();
      if (this.order) await (await this.order).apply(url, request.signal);

      this.restoreGroups(open);
      this.restoreFocus(focus);
      this.announce();

      if (scroll) this.scrollIntoView({ block: 'start' });
    } catch (error) {
      // An abort is the next request taking over; anything else falls
      // back to plain navigation.
      if (error.name !== 'AbortError') window.location.assign(url);
    } finally {
      if (this.pending === request) {
        this.removeAttribute(LOADING);
        this.removeAttribute('aria-busy');
      }
    }
  }

  swap(doc, selector) {
    const next = doc.querySelector(selector);
    const current = this.querySelector(selector);
    if (next && current) current.replaceWith(next);
  }

  // The select outlives the swap, so its value follows the URL by hand.
  // Assigning value never opens the picker.
  syncSort(doc) {
    const next = doc.querySelector('.results__sort-select');
    const current = this.querySelector('.results__sort-select');
    if (next && current && current.value !== next.value) current.value = next.value;
  }

  // Which accordions were open. Only reopening is applied, never closing:
  // one the server opens for holding an active value should stay open.
  openGroups() {
    return Array.from(this.querySelectorAll('.facets__group'), (group) => group.open);
  }

  restoreGroups(state) {
    this.querySelectorAll('.facets__group').forEach((group, index) => {
      if (state[index]) group.open = true;
    });
  }

  // A control in a replaced region takes focus down with it.
  focusTarget() {
    const active = document.activeElement;
    // Sem nome não há como reencontrar, e o <body> nunca tem.
    if (!active?.name || !this.contains(active)) return null;
    // Várias caixas dividem o nome do filtro, então o valor desempata.
    const value = active.type === 'checkbox' ? `[value="${CSS.escape(active.value)}"]` : '';
    return { element: active, key: `[name="${CSS.escape(active.name)}"]${value}` };
  }

  restoreFocus(target) {
    // What survived the swap never lost focus, and refocusing is not
    // harmless: iOS Safari reopens a select's picker on focus.
    if (!target || target.element.isConnected) return;
    // preventScroll, or focus yanks the page back to the control.
    this.querySelector(target.key)?.focus({ preventScroll: true });
  }

  // A swapped grid announces nothing; the count is the one sentence.
  announce() {
    if (!this.status) return;
    this.status.textContent = this.querySelector('.results__count')?.textContent.trim() ?? '';
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
